/*
  Picking up a new deploy without a manual refresh.

  The app is loaded once and then runs from memory, so a tab left open keeps
  showing the old build after the server is updated. Each build writes its id
  to /version.json (a few bytes, served as a plain file); this compares that
  with the id the running code was built with.

  Checked only when moving between pages or coming back to the tab — never on
  a timer — so an idle tab makes no requests at all. Each such moment checks,
  so a deploy is picked up on the next click; a short floor and a single
  request in flight keep fast clicking from repeating it. A newer build is
  loaded on a page change, never in the middle of one, so nothing being typed
  is lost.
*/

const MIN_GAP_MS = 15_000;

// The page has just loaded, so it is current: the first check can wait a moment.
let checkedAt = Date.now();
let newer = false;
let inFlight: Promise<boolean> | null = null;

/** Whether a newer build is known to be on the server. */
export const newerBuildWaiting = (): boolean => newer;

/** Looks for a newer build, unless one was looked for moments ago. Resolves to whether one is waiting. */
export function checkForNewerBuild(): Promise<boolean> {
  if (import.meta.env.DEV || newer) return Promise.resolve(newer);
  if (inFlight) return inFlight;
  if (Date.now() - checkedAt < MIN_GAP_MS) return Promise.resolve(false);
  checkedAt = Date.now();
  inFlight = (async () => {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (res.ok) {
        const { build } = (await res.json()) as { build?: string };
        newer = typeof build === 'string' && build !== __BUILD_ID__;
      }
    } catch {
      // Offline or mid-deploy: try again next time.
    } finally {
      inFlight = null;
    }
    return newer;
  })();
  return inFlight;
}
